import NumberFormat from 'react-number-format';
import { makeStyles } from '@mui/styles';
import { Bolt, CheckBoxOutlineBlankOutlined, CheckBoxOutlined, Event } from '@mui/icons-material';
import { Checkbox, Grid, IconButton, TextField, Tooltip, styled, TextFieldProps } from '@mui/material';
import { DatePicker, DateTimePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { hardCodeRecordFileId } from './constants';
import { FieldDataTypeEnum } from 'ui-component/records/types';
import { AttachmentField } from '../AttachmentField/AttachmentField';

export interface renderInputProps {
    value: string | number;
    onChange: (value: string | number) => void;
    defaultValue?: string | number;
    TextFieldComponent?: (props: TextFieldProps) => JSX.Element;
    TextFieldComponentProps?: Omit<TextFieldProps, 'ref' | 'defaultValue' | 'type'>;
    backgroundAlways?: boolean;
    dataType: FieldDataTypeEnum;
    disabled?: boolean;
    renderFromFieldSettings?: boolean;
    originalName: string;
}

const useStyles = makeStyles((theme) => ({ textareaVResize: { '& textarea': { resize: 'vertical' } } }));

export const BaseTextField = styled(TextField)(({ theme }) => ({
    '& fieldset': {
        border: 'none !important'
    },

    '& :focus-visible, :focus, :active, .MuiInputBase-root .Mui-focused': {
        backgroundColor: `${theme.palette.secondary.light} !important`,
        color: `${theme.palette.secondary.main} !important`,
        '&::placeholder': { color: 'transparent !important' },
        '& svg': {
            color: `${theme.palette.secondary.main} !important`,
            opacity: 0.5
        }
    }
}));

export const getDateByHour = (value: string): Date | null => {
    if (!value || !value.length) return null;
    const [hours, minutes] = value.split(':');
    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0, 0);
    return date;
};

export const StatelessRenderInput = ({
    TextFieldComponent = BaseTextField as (props: TextFieldProps) => JSX.Element,
    TextFieldComponentProps = {},
    backgroundAlways = false,
    dataType,
    defaultValue = '',
    value,
    onChange,
    disabled,
    renderFromFieldSettings,
    originalName
}: renderInputProps) => {
    const classes = useStyles();

    if (
        (
            ['string', 'textarea', 'url', 'dropDown', 'dropdown', 'multiselect', 'multiselect-checkbox'] as Partial<FieldDataTypeEnum[]>
        ).includes(dataType)
    )
        return (
            <TextFieldComponent
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                multiline={dataType === 'textarea'}
                className={dataType === 'textarea' ? classes.textareaVResize : undefined}
                variant="outlined"
                defaultValue={defaultValue}
                fullWidth
                {...TextFieldComponentProps}
            />
        );

    if (
        ((['currency', 'number'] as Partial<FieldDataTypeEnum[]>).includes(dataType) || dataType === 'number') &&
        originalName !== hardCodeRecordFileId
    )
        return (
            <>
                <NumberFormat
                    customInput={TextFieldComponent}
                    value={value as any}
                    onValueChange={(values) => onChange(values.value)}
                    disabled={disabled}
                    {...TextFieldComponentProps}
                    fixedDecimalScale={dataType === 'currency'}
                    thousandSeparator={dataType === 'currency' ? ',' : ''}
                    decimalScale={dataType === 'currency' ? 2 : 0}
                    variant="outlined"
                    defaultValue={defaultValue}
                    fullWidth
                />
            </>
        );

    if (dataType === 'date')
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                {/* @ts-ignore */}
                <DatePicker
                    value={value}
                    disabled={disabled}
                    onChange={(newVal) => {
                        if (newVal) onChange(newVal);
                    }}
                    renderInput={(params) => (
                        <TextFieldComponent
                            fullWidth
                            {...params}
                            {...TextFieldComponentProps}
                            InputProps={{
                                ...TextFieldComponentProps.InputProps,
                                ...params.InputProps
                            }}
                            error={false}
                            variant={TextFieldComponentProps.variant || params.variant}
                            size={TextFieldComponentProps.size || params.size}
                        />
                    )}
                />
            </LocalizationProvider>
        );

    if (dataType === 'datetime') {
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{ todayButtonLabel: 'NOW' }}>
                <DateTimePicker
                    disabled={disabled}
                    onChange={(newVal) => {
                        if (newVal) onChange(newVal);
                    }}
                    value={value}
                    components={{
                        OpenPickerIcon: () => (
                            <Tooltip title="Pick a date">
                                <Event />
                            </Tooltip>
                        )
                    }}
                    componentsProps={{ actionBar: { actions: ['today'] } }}
                    renderInput={(params) => (
                        <TextFieldComponent
                            {...params}
                            {...TextFieldComponentProps}
                            error={false}
                            InputProps={{
                                ...TextFieldComponentProps.InputProps,
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        <Tooltip title="Now">
                                            <IconButton
                                                disabled={disabled}
                                                onClick={() => onChange(new Date().toString())}
                                                sx={{ transform: 'translateX(15px)', marginLeft: '-20px' }}
                                            >
                                                <Bolt />
                                            </IconButton>
                                        </Tooltip>
                                        {params.InputProps?.endAdornment}
                                    </>
                                )
                            }}
                            fullWidth
                        />
                    )}
                />
            </LocalizationProvider>
        );
    }

    if (dataType === 'time') {
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                    onChange={(newValue) => {
                        const isValidDatetime = Date.parse(newValue as string);
                        if (isValidDatetime) {
                            onChange(newValue as string);
                        } else {
                            onChange('');
                        }
                    }}
                    value={!Date.parse(value as string) ? getDateByHour(value as string) : value}
                    renderInput={(params) => <TextFieldComponent fullWidth {...params} {...TextFieldComponentProps} />}
                />
            </LocalizationProvider>
        );
    }

    if (dataType === 'checkbox') {
        return (
            <Grid
                item
                container
                justifyContent={backgroundAlways ? 'center' : 'flex-start'}
                alignItems={backgroundAlways ? 'center' : 'flex-start'}
                xs={12}
                sx={{
                    padding: 0,
                    pt: backgroundAlways ? '0' : '10px',
                    pl: backgroundAlways ? '0' : '25px',
                    borderRadius: '8px',
                    height: '38px',
                    bgcolor: (theme) => (backgroundAlways ? theme.palette.primary[300] : 'inherit')
                }}
            >
                <Checkbox
                    defaultChecked
                    checked={!!value && value === 'true'}
                    onChange={(event) => {
                        onChange(String(event.target.checked));
                    }}
                    sx={(theme) => ({
                        padding: 0,
                        color: theme.palette.primary[400],
                        '&.Mui-checked': { color: theme.palette.primary.dark },
                        '& .MuiSvgIcon-root': { fontSize: 30 }
                    })}
                    icon={<CheckBoxOutlineBlankOutlined />}
                    checkedIcon={<CheckBoxOutlined />}
                />
            </Grid>
        );
    }

    if ((dataType === 'attachment' || originalName === hardCodeRecordFileId) && renderFromFieldSettings) {
        return (
            <TextFieldComponent
                value={value}
                onChange={(e: { target: { value: string | number } }) => onChange(e.target.value)}
                disabled={disabled}
                variant="outlined"
                defaultValue={defaultValue}
                fullWidth
                {...TextFieldComponentProps}
            />
        );
    }

    if (dataType === 'attachment' || originalName === hardCodeRecordFileId) {
        return <AttachmentField disabled />;
    }

    return <></>;
};

export default StatelessRenderInput;
